import React, { useEffect, useState } from 'react';
import { Button, Empty, Spin } from 'antd';

import { AnyObject } from '@triare/auth-redux';
import { LoadingOutlined } from '@ant-design/icons';
import { useLayoutContext } from '../../Layout/context';
import ContentHeader from '../../Common/ContentHeader';
import AnimatedHeight from '../../Common/AnimatedHeight';
import InputSearch from '../../Common/InputSearch';

import SortBy from './SortBy';
import Filter from './Filter';
import { CommentListParams } from '../../../store/comment/list';
import Comment from '../../Common/Comment';
import { useUniqueData } from '../../Common/PostOrCommentTemplate';
import { CommentData, CommentOrderColumn, CommentsGetParams, useCommentsGet } from '../../../hooks/api/comments';
import { useSearchParams } from '../../../hooks/useSearchParams';
import { getValidSearchParams } from '../../../utils';

import styles from './index.module.scss';

export const commentListDefaultSearchParams: Partial<CommentsGetParams> = {
  comment_orderByColumn: CommentOrderColumn.CREATED,
  comment_orderBy: 'DESC',
  comment_search: '',
  comment_updatedAtStart: '',
  comment_updatedAtEnd: '',
};

interface CommentsProps {
  hideTitle?: boolean;
  filter?: Partial<CommentListParams>;
  loading?: boolean;
}

export default function Comments({ hideTitle, loading, filter }: CommentsProps): JSX.Element {
  const { setBreadcrumbs } = useLayoutContext();
  const { searchParams } = useSearchParams();
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const commentsGet = useCommentsGet();
  const [commentPage, setCommentPage] = useState(1);
  const [commentListAcc] = useUniqueData<CommentData>(commentsGet, commentPage);

  const loadComments = () => {
    const params: AnyObject = {
      comment_page: commentPage,
      ...commentListDefaultSearchParams,
      ...getValidSearchParams<CommentsGetParams>(
        (keys) => keys.filter((key) => key.indexOf('comment_') === 0),
        searchParams,
      ),
      ...filter,
    };

    Object.keys(params).forEach((key) => {
      if (key.indexOf('comment_') === 0) {
        params[key.replace('comment_', '')] = params[key];
        delete params[key];
      }
    });

    Object.keys(params).forEach((key) => {
      if (typeof params[key] === 'undefined' || params[key] === '') {
        delete params[key];
      }
    });

    commentsGet.fetch(params);
  };

  useEffect(loadComments, [commentPage, searchParams]);

  useEffect(() => {
    if (!hideTitle) {
      setBreadcrumbs([{ title: 'Comment List' }]);
    }
  }, []);

  return (
    <>
      <ContentHeader title={hideTitle ? '' : 'Comment List'}>
        <div className={styles.formContainer}>
          <Button onClick={() => setIsOpenFilter((prevState) => !prevState)}>Filter</Button>

          <SortBy />
          <InputSearch prefix="comment_" />
        </div>
      </ContentHeader>

      <AnimatedHeight isOpen={isOpenFilter} className={styles.filterWrapper}>
        <Filter onClose={() => setIsOpenFilter(false)} />
      </AnimatedHeight>

      {/* eslint-disable-next-line no-nested-ternary */}
      <div style={hideTitle
        ? (loading ? { minHeight: 'calc(100vh - 280px)' } : undefined)
        : { minHeight: 'calc(100vh - 128px)' }}
      >
        {!commentsGet.loading && commentListAcc.list.length === 0 && commentsGet.data?.data.length === 0 ? (
          <Empty className={styles.marginEmptyPage} />
        ) : (
          (commentListAcc.list.map((comment) => (
            <Comment
              setPage={(newPage) => {
                setCommentPage(newPage);
                loadComments();
              }}
              key={comment.id}
              data={comment}
            />
          )) || [])
        )}
      </div>

      {commentsGet.loading || loading ? (
        <div className="bgSpin">
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </div>
      ) : null}
    </>
  );
}
